import React, { useMemo, useState } from 'react';
import { TextInput } from '@mantine/core';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import { SectionKey } from '../../shared/types/dashboard-page.types';
import { CategorySales } from '../../../../services/reports/reports.service';
import classes from './categories-section.module.scss';

export type CategoriesSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  categorySales: CategorySales[];
};

export const CategoriesSection: React.FC<CategoriesSectionProps> = ({
  collapsedSections,
  toggleSection,
  categorySales,
}) => {
  const [search, setSearch] = useState('');

  const totalSessionAmount = useMemo(() => categorySales.reduce((sum, row) => sum + row.sessionAmount, 0), [categorySales]);

  const visibleCategories = useMemo(
    () => categorySales.filter((row) => row.categoryName.toLowerCase().includes(search.toLowerCase())),
    [categorySales, search],
  );

  return (
    <SectionCard
      title="קטגוריות"
      sectionKey="categories"
      collapsed={collapsedSections.categories}
      onToggle={() => toggleSection('categories')}
      titleExtra={<span className={classes.countBadge}>{`(${categorySales.length} קטגוריות)`}</span>}
    >
      <TextInput
        className={classes.searchInput}
        placeholder="חיפוש לפי שם קטגוריה..."
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        data-testid="dashboard-categories-search"
      />

      {visibleCategories.length === 0 ? (
        <p className={classes.emptyHint}>אין נתונים להצגה</p>
      ) : (
        <div className={classes.list}>
          {visibleCategories.map((row) => {
            const pct = totalSessionAmount > 0 ? Math.round((row.sessionAmount / totalSessionAmount) * 100) : 0;
            return (
              <div key={row.categoryId} className={classes.card}>
                <div className={`${classes.cell} ${classes.cellName}`}>
                  <span className={classes.cellLabel}>שם קטגוריה</span>
                  <span className={classes.cellValue}>{row.categoryName}</span>
                </div>
                <div className={classes.cell}>
                  <span className={classes.cellLabel}>סה"כ מכירות</span>
                  <span className={classes.cellValue}>{asCurrency(row.sessionAmount)}</span>
                </div>
                <div className={classes.cell}>
                  <span className={classes.cellLabel}>אחוז</span>
                  <span className={classes.cellValue}>{`${pct}%`}</span>
                </div>
                <div className={classes.cell}>
                  <span className={classes.cellLabel}>מכירות השבוע</span>
                  <span className={classes.cellValue}>{asCurrency(row.weekAmount)}</span>
                </div>
                <div className={classes.cell}>
                  <span className={classes.cellLabel}>מכירות החודש</span>
                  <span className={classes.cellValue}>{asCurrency(row.monthAmount)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};
